import { type Dispatch, memo, type SetStateAction } from "react";
import "Components/ConfigLayout/Camera/AiAnalytics/AdvancedAiSettings/AdvancedAiSettings.less";
import {
	AspectMaintainer, Button, ButtonTypes, InteractiveSvg, type SelectOption, Select
} from "@clintonelec/react-storybook";
import { produce } from "immer";
import type { AiAnalyticsState } from "Interfaces";
import { StaticObjectFilter, TrackReferencePoint } from "Data/Objects/Camera";
import type { Point } from "@clintonelec/react-storybook";

const staticObjectFilterOptions: SelectOption[] = Object.values(StaticObjectFilter).map((filterDegree) => ({
	label: filterDegree,
	value: filterDegree
}));

const trackReferencePointOptions: SelectOption[] = Object.values(TrackReferencePoint).map((referencePoint) => ({
	label: referencePoint,
	value: referencePoint
}));

type AdvancedAiSettingsProps = {
	localAiAnalyticsState: [ AiAnalyticsState, Dispatch<SetStateAction<AiAnalyticsState>> ];
};

function AdvancedAiSettings(props: AdvancedAiSettingsProps) {
	const { localAiAnalyticsState } = props;
	const [ localAiAnalyticsSettings, setLocalAiAnalyticsSettings ] = localAiAnalyticsState;
	const {
		enabled, staticObjectFilter, trackReferencePoint, maxSizeFilter, minSizeFilter, minSizeFilterEnabled,
		maxSizeFilterEnabled
	} = localAiAnalyticsSettings;

	const handleMinSizeFilterEnabledClick = () => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			draft.minSizeFilterEnabled = !draft.minSizeFilterEnabled;
		}));
	};

	const handleMaxSizeFilterEnabledClick = () => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			draft.maxSizeFilterEnabled = !draft.maxSizeFilterEnabled;
		}));
	};

	const handleStaticObjectFilterSelect = (filterDegree: string | null) => {
		if (!filterDegree) {
			return;
		}

		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			draft.staticObjectFilter = filterDegree as StaticObjectFilter;
		}));
	};

	const handleTrackReferencePointSelect = (referencePoint: string | null) => {
		if (!referencePoint) {
			return;
		}

		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			draft.trackReferencePoint = referencePoint as TrackReferencePoint;
		}));
	};

	const handleMaxFilterPointsChange = (points: [ Point, Point, Point, Point ]) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			draft.maxSizeFilter = points;
		}));
	};

	const handleMinFilterPointsChange = (points: [ Point, Point, Point, Point ]) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			draft.minSizeFilter = points;
		}));
	};

	const renderAdvancedSettings = () => {
		if (!enabled) {
			return (
				<div className="enable-message">Enable AI Analytics to adjust rule settings.</div>
			);
		}

		return (
			<div className="settings">
				<div className="form-row">
					<span>Static Object Filter</span>
					<Select
						allowClear={ false }
						onSelect={ handleStaticObjectFilterSelect }
						options={ staticObjectFilterOptions }
						value={ staticObjectFilter }
					/>
				</div>
				<div className="form-row">
					<span>Track Reference Point</span>
					<Select
						allowClear={ false }
						onSelect={ handleTrackReferencePointSelect }
						options={ trackReferencePointOptions }
						value={ trackReferencePoint }
					/>
				</div>
			</div>
		);
	};

	const renderMinSizeFilter = () => {
		if (!minSizeFilterEnabled) {
			return null;
		}

		return (
			<InteractiveSvg.Rectangle
				color="#33b889"
				points={ minSizeFilter }
				onChange={ handleMinFilterPointsChange } />
		);
	};

	const renderMaxSizeFilter = () => {
		if (!maxSizeFilterEnabled) {
			return null;
		}

		return (
			<InteractiveSvg.Rectangle
				color="#f4ce34"
				points={ maxSizeFilter }
				onChange={ handleMaxFilterPointsChange } />
		);
	};

	const renderInteractivePolygon = () => {
		if (!enabled) {
			return;
		}

		return (
			<InteractiveSvg>
				{ renderMinSizeFilter() }
				{ renderMaxSizeFilter() }
			</InteractiveSvg>
		);
	};

	return (
		<div className="advanced-settings two-column-card-content">
			<div className="column left-content">
				<div className="camera-container">
					<AspectMaintainer>
						<div className="camera-snapshot">
							{ renderInteractivePolygon() }
						</div>
					</AspectMaintainer>
				</div>
				<div className="filter-buttons">
					<Button
						className="filter-button"
						disabled={ !enabled }
						ghost={ !minSizeFilterEnabled }
						htmlType="button"
						onClick={ handleMinSizeFilterEnabledClick }
						type={ ButtonTypes.PRIMARY }
					>
						Min Size Filter
					</Button>
					<Button
						className="filter-button"
						disabled={ !enabled }
						ghost={ !maxSizeFilterEnabled }
						htmlType="button"
						onClick={ handleMaxSizeFilterEnabledClick }
						type={ ButtonTypes.WARNING }
					>
						Max Size Filter
					</Button>
				</div>
			</div>
			<div className="column right-content">
				{ renderAdvancedSettings() }
			</div>
		</div>
	);
}

export default memo(AdvancedAiSettings);
